import React, { forwardRef, useState } from 'react';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import { Notifications as Nav4Notifications } from '@atlaskit/navigation-system';
import { SPOTLIGHT_MESSAGE_ID } from '@atlassian/jira-cross-flow-using-notifications-to-improve-admin-approval/src/controllers/constants.tsx';
import {
	useUsingNotificationsToImproveAdminApprovalContext,
	CoordinationStopProvider,
} from '@atlassian/jira-cross-flow-using-notifications-to-improve-admin-approval/src/controllers/index.tsx';
import { UsingNotificationsToImproveAdminApprovalSpotlight } from '@atlassian/jira-cross-flow-using-notifications-to-improve-admin-approval/src/ui/index.tsx';
import CoordinationClient from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { ExperimentalNotificationProps } from '../../types.tsx';

// Should this live in the migration layer (@atlassian/jira-navigation-system) instead?
const renderNotifications = (
	props: ExperimentalNotificationProps,
	ref: React.ForwardedRef<HTMLElement>,
	shouldShowRequestSpotlight: boolean,
) => {
	const { badge, onClick, isSelected, label } = props;

	// When we are showing the spotlight, the role listitem is applied to the SpotlightTarget component (near line 77),
	// hence we don't need to set Nav4Notifications as a listitem in here.
	const isListItem = !shouldShowRequestSpotlight;

	return (
		<Nav4Notifications
			isListItem={isListItem}
			badge={badge}
			label={label}
			onClick={onClick}
			isSelected={isSelected}
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- PLEASE FIX - ENABLING FLAT LINT CONFIG
			ref={ref as React.ForwardedRef<HTMLButtonElement>}
		/>
	);
};

export const Notification = forwardRef<HTMLElement, ExperimentalNotificationProps>((props, ref) => {
	const { markSpotlightAsShown, hasSpotlightBeenShown } =
		useUsingNotificationsToImproveAdminApprovalContext();

	const [isSpotlightActive, setIsSpotlightActive] = useState(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSiteAdmin } = useTenantContext();

	const { onViewRequests, ...notificationProps } = props;

	/**
	 * Send analytics event when user clicks "Close" or "View requests" button on the request spotlight
	 * Event registry - https://data-portal.internal.atlassian.com/analytics/registry/52348
	 * @param {"close"|"viewRequests"} buttonType
	 */
	const handleSendAnalyticsEventForClickRequestSpotlightButton = (
		buttonType: 'close' | 'viewRequests',
	) => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(analyticsEvent, 'requestSpotlightButton', {
			buttonType,
			isSiteAdmin,
		});
	};

	const shouldShowRequestsSpotlight = isSiteAdmin && !hasSpotlightBeenShown;

	if (!shouldShowRequestsSpotlight) {
		return renderNotifications(notificationProps, ref, shouldShowRequestsSpotlight);
	}

	return (
		<SpotlightManager>
			<div role="listitem">
				<SpotlightTarget name="admin-request-spotlight">
					{renderNotifications(notificationProps, ref, shouldShowRequestsSpotlight)}
				</SpotlightTarget>
			</div>
			<SpotlightTransition>
				{isSpotlightActive && (
					<CoordinationClient messageId={SPOTLIGHT_MESSAGE_ID} messageType="engagement">
						<CoordinationStopProvider>
							{(stop) => (
								<UsingNotificationsToImproveAdminApprovalSpotlight
									onViewRequests={() => {
										setIsSpotlightActive(false);
										markSpotlightAsShown();
										onViewRequests && onViewRequests();
										handleSendAnalyticsEventForClickRequestSpotlightButton('viewRequests');
										stop && stop();
									}}
									onClose={() => {
										setIsSpotlightActive(false);
										markSpotlightAsShown();
										handleSendAnalyticsEventForClickRequestSpotlightButton('close');
										stop && stop();
									}}
								/>
							)}
						</CoordinationStopProvider>
					</CoordinationClient>
				)}
			</SpotlightTransition>
		</SpotlightManager>
	);
});
